import type { ConsentKitConfig, Regime, ResolvedRegion } from "../types";

declare global {
  interface Navigator {
    /**
     * Global Privacy Control. Not in lib.dom yet, but shipped by Firefox, Brave, DuckDuckGo
     * and every major privacy extension, and legally binding in a growing list of US states.
     */
    globalPrivacyControl?: boolean;
  }
}

/**
 * Canonical IANA zones for the fifty states, DC and the territories that follow US law.
 * A zone list is the only signal a client-side script has: there is no geolocation here, no
 * IP lookup, and no request to anyone — which is the point, since asking a third party
 * where the visitor is would itself be the thing consent is about.
 */
const US_TIME_ZONES = new Set([
  "America/New_York",
  "America/Detroit",
  "America/Kentucky/Louisville",
  "America/Kentucky/Monticello",
  "America/Indiana/Indianapolis",
  "America/Indiana/Vincennes",
  "America/Indiana/Winamac",
  "America/Indiana/Marengo",
  "America/Indiana/Petersburg",
  "America/Indiana/Vevay",
  "America/Chicago",
  "America/Indiana/Tell_City",
  "America/Indiana/Knox",
  "America/Menominee",
  "America/North_Dakota/Center",
  "America/North_Dakota/New_Salem",
  "America/North_Dakota/Beulah",
  "America/Denver",
  "America/Boise",
  "America/Phoenix",
  "America/Los_Angeles",
  "America/Anchorage",
  "America/Juneau",
  "America/Sitka",
  "America/Metlakatla",
  "America/Yakutat",
  "America/Nome",
  "America/Adak",
  "Pacific/Honolulu",
]);

/**
 * Turns the configured region into the one that applies to this visitor.
 *
 * `auto` is deliberately biased: anything the time-zone list does not positively identify as
 * the United States is treated as EU, so an unrecognised, spoofed or missing zone lands the
 * visitor in the opt-in regime. Guessing wrong that way costs a banner; guessing wrong the
 * other way sets a tracker without consent.
 */
export function resolveRegion(config: ConsentKitConfig): ResolvedRegion {
  const inferred = config.region === "auto";
  const region = inferred ? detectRegion() : (config.region as "eu" | "us");
  const regime: Regime = region === "us" ? "opt-out" : "opt-in";

  return { region, regime, gpc: hasGpcSignal(config, regime), inferred };
}

/**
 * Only meaningful in the opt-out regime. Under the DSGVO nothing runs before consent
 * anyway, so a GPC signal there would change nothing that is not already the default —
 * and silently converting it into a stored "rejected" decision would put words in the
 * visitor's mouth that Art. 7 requires to be their own.
 */
function hasGpcSignal(config: ConsentKitConfig, regime: Regime): boolean {
  if (regime !== "opt-out" || !config.honorGpc) return false;
  return navigator.globalPrivacyControl === true;
}

function detectRegion(): "eu" | "us" {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return zone && US_TIME_ZONES.has(zone) ? "us" : "eu";
  } catch {
    return "eu";
  }
}
